import { useEffect, useState } from "react";
import { api, formatApiError, formatINR } from "@/lib/api";
import SEOHead from "@/components/SEOHead";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Building2, Loader2 } from "lucide-react";

const EMPTY = {
  name: "", courses: "", feesMin: "", feesMax: "", eligibility: "", websiteUrl: "",
  address: "", contactNumber: "", description: "", naacGrade: "A", placementHighlights: "",
  logoUrl: "", coverUrl: "", isActive: true,
};

export default function AdminColleges() {
  const [colleges, setColleges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => { setLoading(true); api.get("/admin/colleges").then((r) => setColleges(r.data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditId(null); setForm(EMPTY); setModal(true); };
  const openEdit = (c) => {
    setEditId(c.id);
    setForm({
      name: c.name, courses: (c.courses || []).join(", "), feesMin: c.fees?.min ?? "", feesMax: c.fees?.max ?? "",
      eligibility: c.eligibility || "", websiteUrl: c.websiteUrl || "", address: c.address || "",
      contactNumber: c.contactNumber || "", description: c.description || "", naacGrade: c.naacGrade || "A",
      placementHighlights: c.placementHighlights || "", logoUrl: c.logoUrl || "", coverUrl: c.coverUrl || "", isActive: c.isActive,
    });
    setModal(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name is required."); return; }
    setSaving(true);
    const payload = {
      name: form.name,
      courses: form.courses.split(",").map((s) => s.trim()).filter(Boolean),
      fees: { min: Number(form.feesMin) || 0, max: Number(form.feesMax) || 0 },
      eligibility: form.eligibility, websiteUrl: form.websiteUrl, address: form.address,
      contactNumber: form.contactNumber, description: form.description, naacGrade: form.naacGrade,
      placementHighlights: form.placementHighlights, logoUrl: form.logoUrl, coverUrl: form.coverUrl, isActive: form.isActive,
    };
    try {
      if (editId) await api.patch(`/admin/colleges/${editId}`, payload);
      else await api.post("/admin/colleges", payload);
      toast.success(editId ? "College updated" : "College added");
      setModal(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const del = async (c) => {
    if (!window.confirm(`Delete "${c.name}"?`)) return;
    try {
      await api.delete(`/admin/colleges/${c.id}`);
      toast.success("College deleted");
      load();
    } catch (e) {
      toast.error("Delete failed.");
    }
  };

  return (
    <div data-testid="admin-colleges-page">
      <SEOHead title="Colleges" />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Colleges</h1>
          <p className="text-sm text-slate-500">{colleges.length} colleges</p>
        </div>
        <button data-testid="add-college-btn" onClick={openNew} className="inline-flex items-center gap-2 rounded-lg bg-[#0B2A6B] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#081E4D]">
          <Plus className="h-4 w-4" /> Add College
        </button>
      </div>

      <div className="mt-6 overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full min-w-[680px] text-left">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <th className="p-4">Name</th><th className="p-4">NAAC</th><th className="p-4">Fees</th><th className="p-4">Active</th><th className="p-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="p-10 text-center text-slate-400">Loading…</td></tr>
            ) : colleges.map((c) => (
              <tr key={c.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50" data-testid={`college-row-${c.slug}`}>
                <td className="p-4"><p className="font-medium text-slate-900">{c.name}</p><p className="text-xs text-slate-400">{(c.courses || []).slice(0, 3).join(", ")}</p></td>
                <td className="p-4 text-slate-600">{c.naacGrade}</td>
                <td className="p-4 text-slate-600 whitespace-nowrap">₹{formatINR(c.fees?.min)} – ₹{formatINR(c.fees?.max)}</td>
                <td className="p-4">{c.isActive ? <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700">Active</span> : <span className="rounded-full bg-slate-200 px-2 py-0.5 text-xs text-slate-600">Hidden</span>}</td>
                <td className="p-4 text-right whitespace-nowrap">
                  <button data-testid={`edit-college-${c.slug}`} onClick={() => openEdit(c)} className="mr-2 inline-flex items-center gap-1 text-sm font-semibold text-[#0B2A6B] hover:underline"><Pencil className="h-3.5 w-3.5" /> Edit</button>
                  <button data-testid={`delete-college-${c.slug}`} onClick={() => del(c)} className="inline-flex items-center gap-1 text-sm font-semibold text-red-600 hover:underline"><Trash2 className="h-3.5 w-3.5" /> Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4" data-testid="college-modal">
          <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2"><Building2 className="h-5 w-5 text-[#0B2A6B]" /> {editId ? "Edit College" : "Add College"}</h2>
              <button onClick={() => setModal(false)} className="text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={save} className="mt-5 grid sm:grid-cols-2 gap-4">
              <F label="Name *" className="sm:col-span-2"><input data-testid="college-form-name" className="ci" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></F>
              <F label="Courses (comma separated)" className="sm:col-span-2"><input data-testid="college-form-courses" className="ci" value={form.courses} onChange={(e) => setForm({ ...form, courses: e.target.value })} placeholder="B.Tech, MBA" /></F>
              <F label="Fees Min"><input type="number" className="ci" value={form.feesMin} onChange={(e) => setForm({ ...form, feesMin: e.target.value })} /></F>
              <F label="Fees Max"><input type="number" className="ci" value={form.feesMax} onChange={(e) => setForm({ ...form, feesMax: e.target.value })} /></F>
              <F label="NAAC Grade"><select className="ci" value={form.naacGrade} onChange={(e) => setForm({ ...form, naacGrade: e.target.value })}>{["A++", "A+", "A", "B++", "B+"].map((g) => <option key={g}>{g}</option>)}</select></F>
              <F label="Contact Number"><input className="ci" value={form.contactNumber} onChange={(e) => setForm({ ...form, contactNumber: e.target.value })} /></F>
              <F label="Website URL" className="sm:col-span-2"><input className="ci" value={form.websiteUrl} onChange={(e) => setForm({ ...form, websiteUrl: e.target.value })} /></F>
              <F label="Address" className="sm:col-span-2"><input className="ci" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></F>
              <F label="Eligibility" className="sm:col-span-2"><input className="ci" value={form.eligibility} onChange={(e) => setForm({ ...form, eligibility: e.target.value })} /></F>
              <F label="Placement Highlights" className="sm:col-span-2"><input className="ci" value={form.placementHighlights} onChange={(e) => setForm({ ...form, placementHighlights: e.target.value })} /></F>
              <F label="Cover Image URL" className="sm:col-span-2"><input className="ci" value={form.coverUrl} onChange={(e) => setForm({ ...form, coverUrl: e.target.value })} /></F>
              <F label="Description" className="sm:col-span-2"><textarea rows={3} className="ci !h-auto py-2" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></F>
              <label className="sm:col-span-2 flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} /> Active (visible on public site)</label>
              <div className="sm:col-span-2 flex justify-end gap-2">
                <button type="button" onClick={() => setModal(false)} className="rounded-lg border border-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
                <button data-testid="college-form-save" disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-[#0B2A6B] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#081E4D] disabled:opacity-60">
                  {saving ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</> : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <style>{`.ci{display:block;height:2.75rem;width:100%;border-radius:0.5rem;border:1px solid #cbd5e1;background:#fff;padding:0 0.875rem;font-size:0.875rem;outline:none}.ci:focus{box-shadow:0 0 0 2px #0B2A6B;border-color:transparent}`}</style>
    </div>
  );
}

function F({ label, children, className = "" }) {
  return <label className={`block ${className}`}><span className="text-sm font-medium text-slate-700">{label}</span><div className="mt-1.5">{children}</div></label>;
}
